import React, { useMemo } from 'react';
import { Form, Button } from 'react-bootstrap';
import ImageUploader from './ImageUploader';

// Editor for a page components collection shaped as:
// { items: [ { type: string, positionSmallScreen?: 'top'|'bottom'|'hidden', positionWideScreen?: 'top'|'bottom'|'hidden'|'right'|'left', images?: [ { src: string, title?: string } ] } ] }
// Props:
// - value: current object (optional)
// - onChange: function(nextValue)
// - websiteId: required by ImageUploader
// - className?: optional css class

function normalize(value) {
  const base = value && typeof value === 'object' ? value : {};
  const items = Array.isArray(base.items) ? base.items : [];
  const allowedSmall = ['top', 'bottom', 'hidden'];
  const allowedWide = ['top', 'bottom', 'hidden', 'right', 'left'];
  const normItems = items.map((it) => {
    const type = String(it?.type || '');
    // Use camelCase only (no backward-compat)
    const psCandidate = it?.positionSmallScreen || 'hidden';
    const pwCandidate = it?.positionWideScreen || 'hidden';
    const psRaw = String(psCandidate);
    const pwRaw = String(pwCandidate);
    const positionSmallScreen = allowedSmall.includes(psRaw) ? psRaw : 'hidden';
    const positionWideScreen = allowedWide.includes(pwRaw) ? pwRaw : 'hidden';
    const images = Array.isArray(it?.images)
      ? it.images.map((img) => ({ src: String(img?.src || ''), title: img?.title ? String(img.title) : '' }))
      : [];
    return { type, positionSmallScreen, positionWideScreen, images };
  });
  return { items: normItems };
}

const emptyItem = { type: 'gallery', positionSmallScreen: 'hidden', positionWideScreen: 'hidden', images: [] };

export default function ComponentsCollectionEditor({ value, onChange, websiteId, className = '' }) {
  const data = useMemo(() => normalize(value), [value]);

  const emit = (next) => onChange?.(normalize(next));

  const addItem = () => emit({ items: [...data.items, { ...emptyItem }] });
  const removeItem = (idx) => emit({ items: data.items.filter((_, i) => i !== idx) });
  const moveItem = (idx, dir) => {
    const arr = [...data.items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    const tmp = arr[idx];
    arr[idx] = arr[j];
    arr[j] = tmp;
    emit({ items: arr });
  };
  const updateItemField = (idx, field, val) => {
    const arr = data.items.map((it, i) => (i === idx ? { ...it, [field]: val } : it));
    emit({ items: arr });
  };
  const addImage = (idx, img) => {
    const arr = data.items.map((it, i) => (i === idx ? { ...it, images: [...(it.images || []), img] } : it));
    emit({ items: arr });
  };
  const updateImage = (idx, imgIdx, field, val) => {
    const arr = data.items.map((it, i) => {
      if (i !== idx) return it;
      const imgs = (it.images || []).map((im, j) => (j === imgIdx ? { ...im, [field]: val } : im));
      return { ...it, images: imgs };
    });
    emit({ items: arr });
  };
  const removeImage = (idx, imgIdx) => {
    const arr = data.items.map((it, i) => {
      if (i !== idx) return it;
      const imgs = (it.images || []).filter((_, j) => j !== imgIdx);
      return { ...it, images: imgs };
    });
    emit({ items: arr });
  };

  return (
    <div className={className}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Form.Label style={{ margin: 0 }}>Components</Form.Label>
        <Button size="sm" variant="secondary" onClick={addItem}>Add item</Button>
      </div>

      {data.items.length === 0 && (
        <div className="text-muted" style={{ marginBottom: 8 }}>No items yet</div>
      )}

      {data.items.map((it, idx) => (
        <div key={idx} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 12, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <Form.Group style={{ minWidth: 160 }}>
              <Form.Label style={{ fontSize: 12, marginBottom: 4 }}>Type</Form.Label>
              <Form.Control as="select" value={it.type} onChange={(e) => updateItemField(idx, 'type', e.target.value)}>
                <option value="gallery">gallery</option>
                <option value="custom">custom</option>
              </Form.Control>
            </Form.Group>

              <Form.Group>
                <Form.Label style={{ fontSize: 12, marginBottom: 4 }}>Position (small)</Form.Label>
                <Form.Control as="select" value={it.positionSmallScreen} onChange={(e) => updateItemField(idx, 'positionSmallScreen', e.target.value)}>
                  <option value="hidden">hidden</option>
                  <option value="top">top</option>
                  <option value="bottom">bottom</option>

                </Form.Control>
              </Form.Group>

              <Form.Group>
                <Form.Label style={{ fontSize: 12, marginBottom: 4 }}>Position (wide)</Form.Label>
                <Form.Control as="select" value={it.positionWideScreen} onChange={(e) => updateItemField(idx, 'positionWideScreen', e.target.value)}>
                  <option value="hidden">hidden</option>
                  <option value="top">top</option>
                  <option value="bottom">bottom</option>
                  <option value="right">right</option>
                  <option value="left">left</option>
                </Form.Control>
              </Form.Group>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <Button size="sm" variant="outline-secondary" onClick={() => moveItem(idx, -1)} disabled={idx === 0}>↑</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => moveItem(idx, 1)} disabled={idx === data.items.length - 1}>↓</Button>
              <Button size="sm" variant="outline-danger" onClick={() => removeItem(idx)}>Remove</Button>
            </div>
          </div>

          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontWeight: 500 }}>Images</div>
              <ImageUploader
                websiteId={websiteId}
                onUploaded={(resp) => {
                  const url = resp?.url || resp?.data?.url || resp?.file?.url || '';
                  if (!url) return;
                  addImage(idx, { src: url, title: '' });
                }}
                label="Upload image"
              />
            </div>

            {(it.images || []).length === 0 && (
              <div className="text-muted" style={{ marginBottom: 6 }}>No images</div>
            )}

            {(it.images || []).map((img, imgIdx) => (
              <div key={imgIdx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <img src={img.src} alt={img.title || `img-${imgIdx}`} style={{ height: 40, width: 60, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} />
                <Form.Control
                  type="text"
                  value={img.title || ''}
                  onChange={(e) => updateImage(idx, imgIdx, 'title', e.target.value)}
                  placeholder="Image title"
                />
                <Button size="sm" variant="outline-danger" onClick={() => removeImage(idx, imgIdx)}>Remove</Button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
